/**
 * RestDatabaseAdapter
 *
 * Database adapter that calls the REST API server.
 * Used for web browser development when not running in Electron.
 *
 * Implements IDatabasePort interface for the hexagonal architecture.
 */

import { getAPIBaseUrl } from '../../environment'

const API_BASE = getAPIBaseUrl()

/**
 * Get auth headers for API requests
 * @param {boolean} includeContentType - Whether to include Content-Type header (default true for POST/PUT, false for GET/DELETE)
 * @returns {Object} Headers object with Authorization if token exists
 */
function getAuthHeaders(includeContentType = true) {
  const token = localStorage.getItem('modernizeit_token')
  const headers = {}

  if (includeContentType) {
    headers['Content-Type'] = 'application/json'
  }

  if (token && token.length > 100) {
    headers['Authorization'] = `Bearer ${token}`
  }

  return headers
}

// Shared 401 handler — clears session and redirects to login exactly once.
// Called from every HTTP method so the 401-loop can't persist.
async function handleHttpError(res, context) {
  const error = await res.json().catch(() => ({ detail: `HTTP ${res.status}: ${res.statusText}` }))
  if (res.status === 401) {
    console.warn(`[RestDB] 401 on ${context} — clearing session`)
    localStorage.removeItem('modernizeit_token')
    localStorage.removeItem('modernizeit_user')
    window.dispatchEvent(new CustomEvent('auth-logout', { detail: { reason: 'token_expired' } }))
    const err = new Error(error.detail || 'Session expired — please log in again')
    err.isAuthError = true
    throw err
  }
  throw new Error(error.detail || `${context} failed: ${res.status}`)
}

async function get(path) {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: getAuthHeaders(false)  // GET doesn't need Content-Type
  })
  if (!res.ok) await handleHttpError(res, `GET ${path}`)
  return res.json()
}

async function post(path, data) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: getAuthHeaders(true),  // POST needs Content-Type
    body: JSON.stringify(data),
  })
  if (!res.ok) await handleHttpError(res, `POST ${path}`)
  return res.json()
}

async function put(path, data) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'PUT',
    headers: getAuthHeaders(true),  // PUT needs Content-Type
    body: JSON.stringify(data),
  })
  if (!res.ok) await handleHttpError(res, `PUT ${path}`)
  return res.json()
}

async function del(path) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'DELETE',
    headers: getAuthHeaders(false)  // DELETE doesn't need Content-Type
  })
  if (!res.ok) await handleHttpError(res, `DELETE ${path}`)
  return res.json()
}

export const restDatabaseAdapter = {
  // Accounts
  getAccounts: async () => {
    console.log('[RestDB] Fetching accounts from GET /accounts')
    const response = await get('/accounts')
    const accounts = response.accounts || []
    console.log(`[RestDB] GET /accounts returned ${accounts.length} accounts:`, accounts.map(a => ({ id: a.id, name: a.name })))
    return accounts
  },
  getAccount: (id) => get(`/accounts/${id}`),
  getDefaultAccount: () => get('/accounts/default'),
  createAccount: async (data) => {
    console.log('[RestDB] Creating account via POST /accounts/sync:', data)

    // Convert camelCase to snake_case for API compatibility
    const apiData = {
      name: data.name,
      description: data.description || '',
      storage_type: data.storageType || 's3',
      s3_bucket: data.s3Bucket,
      s3_region: data.s3Region || 'us-east-1',
      s3_prefix: data.s3Prefix || '',
      local_path: data.localPath,
      // Include scoutAccountId only if editing (for updates)
      ...(data.scoutAccountId && { account_id: data.scoutAccountId })
    }

    console.log('[RestDB] Converted to API format (snake_case):', apiData)
    const response = await post('/accounts/sync', apiData)
    console.log('[RestDB] Account created successfully:', response)
    return response
  },
  updateAccount: (id, data) => put(`/accounts/${id}`, data),
  deleteAccount: async (id) => {
    console.log(`[RestDB] Deleting account via DELETE /accounts/${id}`)
    const response = await del(`/accounts/${id}`)
    console.log('[RestDB] Account deleted successfully:', response)
    return response
  },

  // Applications
  getApplications: (accountId) => get(`/applications${accountId ? `?accountId=${accountId}` : ''}`),
  getApplication: (id) => get(`/applications/${id}`),
  createApplication: (data) => post('/applications', data),
  updateApplication: (id, data) => put(`/applications/${id}`, data),
  deleteApplication: (id) => del(`/applications/${id}`),

  // Jobs
  getJobs: (applicationId) => get(`/jobs?applicationId=${applicationId}`),
  getJob: (id) => get(`/jobs/${id}`),
  createJob: (data) => post('/jobs', data),
  updateJob: (id, data) => put(`/jobs/${id}`, data),

  // Workflows
  getWorkflows: (applicationId) => get(`/workflows?applicationId=${applicationId}`),
  getWorkflow: (id) => get(`/workflows/${id}`),
  createWorkflow: (data) => post('/workflows', data),
  updateWorkflow: (id, data) => put(`/workflows/${id}`, data),
  deleteWorkflow: (id) => del(`/workflows/${id}`),

  // Settings
  getSettings: () => get('/settings'),
  updateSettings: (data) => put('/settings', data),
  getAWSCredentials: () => get('/aws-credentials'),
  updateAWSCredentials: (data) => put('/aws-credentials', data),
  getAIProvider: (provider) => get(`/ai-providers/${provider}`),
  updateAIProvider: (provider, data) => put(`/ai-providers/${provider}`, data),

  // File Storage Settings
  getFileStorageSettings: () => get('/file-storage-settings'),
  updateFileStorageSettings: (data) => put('/file-storage-settings', data),
}
